import React from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  SvgIcon,
} from '@mui/material';

import { borderRadius, palette, spaces, typography } from '../../assets/styles/themes/tokens';
import Truncate from '../Truncate';
import getMuiIcon from '../../utils/getMuiIcon';
import { ContentItem } from '../../types/AppContent';
import OwnCollectionOverviewWatchlistItemMetadata from './OwnCollectionOverviewWatchlistItemMetadata';

interface OwnCollectionOverviewWatchlistItemProps {
  item: ContentItem,
  isSelected: boolean;
  onItemClick: () => void;
}

const OwnCollectionOverviewWatchlistItem = ({
  item,
  isSelected,
  onItemClick
}: OwnCollectionOverviewWatchlistItemProps) => {
  return (
    <Card
      data-testid={`test-item-${item.id}`}
      key={item.id}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${palette.elevation.outlined}`,
        borderRadius: borderRadius.sm,
        padding: spaces.sm,
        backgroundColor: isSelected ? palette.primary.selected : palette.background.paperElevation1,
        gap: spaces.sm,
        '&:hover': {
          backgroundColor: palette.primary.hover,
          '.truncate-lines-2.watchlist-item-title': {
            color: palette.primary.main,
          },
        },
        '&:focus, &:focus-within, &:focus-visible': {
          backgroundColor: palette.primary.focusVisible,
          borderColor: palette.primary.focusVisible,
          border: `2px solid ${palette.primary.main}`,
          outlineOffset: spaces.xxxs,
          '.truncate-lines-2.watchlist-item-title': {
            color: palette.primary.main,
          },
        },
      }}
      onClick={onItemClick}
    >
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          justifyContent: 'space-evenly',
        }}
      >
        <CardMedia
          component='img'
          height='140'
          image={item.thumbnail}
          alt={item.title}
          sx={{
            width: 'auto',
            maxWidth: '100%',
            borderRadius: borderRadius.sm,
          }}
        />
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 48,
          height: 48,
          borderRadius: '50%',
          backgroundColor: palette.secondary.main,
        }}>
          <SvgIcon
            component={getMuiIcon(isSelected ? 'addedCollection' : 'addCollectionIcon')}
            viewBox='0 0 24 24'
            sx={{
              stroke: palette.primary.contrast,
              position: 'absolute',
              top: isSelected ? '50%' : '60%',
              left: isSelected ? '50%' : '60%',
              transform: 'translate(-50%, -50%)',
            }}
          />
        </Box>
      </Box>
      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          paddingX: 0,
          paddingY: spaces.xs,
          height: '140px',
          '&:last-child': {
            paddingBottom: 0
          },
        }}
      >
        <Typography
          variant='overline'
          sx={{
            display: 'block',
            color: palette.text.secondary,
            textTransform: 'uppercase',
            paddingBottom: spaces.xs,
          }}
        >
          {item.publisher}
        </Typography>
        <Truncate
          className='watchlist-item-title'
          text={item.title}
          sx={{
            ...typography.h6,
            color: palette.text.primary,
            paddingBottom: spaces.xxs,
          }}
          showTitle={true}
          lines={2}
        />
        <Typography
          variant='caption'
          sx={{
            display: 'block',
            color: palette.text.secondary,
          }}
        >
          {item.author}
        </Typography>
        <OwnCollectionOverviewWatchlistItemMetadata
          schoolTypes={item.schoolTypes}
          subjects={item.subjects}
          classYears={item.classYears}
        />
      </CardContent>
    </Card>
  );
};

export default OwnCollectionOverviewWatchlistItem;
